import { memo, useCallback } from "react";
import {
  CardNumber,
  ExpirationDate,
  SecurityCode,
} from "@mercadopago/sdk-react";
import styles from "./MpCardSecureFields.module.css";

const MP_FIELD_STYLE = {
  width: "100%",
  height: "40px",
} as const;

export type SecureFieldName =
  | "cardNumber"
  | "securityCode"
  | "expirationDate";

export type OnSecureFieldValidity = (
  field: SecureFieldName,
  errorMessages: Array<{ message: string }>
) => void;

type Props = {
  onFieldValidity: OnSecureFieldValidity;
};

/**
 * Isolado com React.memo: re-renders do pai (nome, CPF, etc.) não
 * reexecutam estes componentes, evitando remontar os iframes e apagar o conteúdo.
 * A validade deve ser guardada com useRef no pai, não com setState, para que
 * onValidityChange (blur) também não dispare re-render.
 */
const MpCardSecureFields = memo(function MpCardSecureFields({ onFieldValidity }: Props) {
  const onCard = useCallback(
    (e: { errorMessages: Array<{ message: string }> }) => {
      onFieldValidity("cardNumber", e.errorMessages);
    },
    [onFieldValidity]
  );

  const onExpiry = useCallback(
    (e: { errorMessages: Array<{ message: string }> }) => {
      onFieldValidity("expirationDate", e.errorMessages);
    },
    [onFieldValidity]
  );

  const onCvv = useCallback(
    (e: { errorMessages: Array<{ message: string }> }) => {
      onFieldValidity("securityCode", e.errorMessages);
    },
    [onFieldValidity]
  );

  return (
    <div className={styles.grid}>
      <div className={styles.rowCardFields}>
        <div className={styles.col}>
          <label className={styles.fieldLabel} htmlFor="mp-cardnum-slot">
            Número do cartão
          </label>
          <div id="mp-cardnum-slot" className={styles.fieldBox}>
            <CardNumber
              placeholder="1234 1234 1234 1234"
              style={MP_FIELD_STYLE}
              onValidityChange={onCard}
            />
          </div>
        </div>
        <div className={styles.col}>
          <label className={styles.fieldLabel} htmlFor="mp-exp-slot">
            Vencimento
          </label>
          <div id="mp-exp-slot" className={styles.fieldBox}>
            <ExpirationDate
              placeholder="MM/AA"
              style={MP_FIELD_STYLE}
              onValidityChange={onExpiry}
            />
          </div>
        </div>
        <div className={styles.col}>
          <label className={styles.fieldLabel} htmlFor="mp-cvv-slot">
            CVV
          </label>
          <div id="mp-cvv-slot" className={styles.fieldBox}>
            <SecurityCode
              placeholder="123"
              style={MP_FIELD_STYLE}
              onValidityChange={onCvv}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default MpCardSecureFields;
